import React, { useState } from "react";
import { Formik, Form } from "formik";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Stack,
  Paper,
  InputAdornment,
  CircularProgress,
  Alert,
  Snackbar,
} from "@mui/material";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import {
  loginInitialValues,
  LoginSchema,
} from "../../validation/account.validation";
import backgroundImg from "../../assets/background-img.jpeg";
import logo from "../../assets/Logo.jpg";
import LoginIcon from "@mui/icons-material/Login";
import { getLoginUserData, loginUser, removeAccessToken, storeAccessToken, verifyEmailOTP, uploadNcnndaAgreement } from "../../services/account.service";
import { loginMessages } from "../../constants/message.constant";
import { useNavigate } from "react-router-dom";
import {ROLES} from "../../constants/general.constant";
import DigitalSignModal from "./digitalSignin";
import {businessUploadNcnndaAgreement} from "../../services/business.service";
import { useUser } from "../../context/UserContext";

const UserLoginForm: React.FC = () => {
  const navigate = useNavigate();
  const { setUser } = useUser();
  const [showPassword, setShowPassword] = useState(false);
  const [showSignModal, setShowSignModal] = useState(false);
  const [emailVerificationRequired, setEmailVerificationRequired] = useState(false);
  const [loginOtp, setLoginOtp] = useState("");
  const [pendingCredentials, setPendingCredentials] = useState<any>(null);
  const [isVerifyingEmail, setIsVerifyingEmail] = useState(false);

  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });
  const [isSignatureUploading, setIsSignatureUploading] = useState(false);

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const redirectToUser = (userData: any) => {
    if (userData && userData.role === ROLES.EMPLOYEE) {
      navigate(`/employee/leads`);
    } else if (userData && userData.role === ROLES.CO_OWNER) {
      navigate(`/co-owner/leads`);
    } else if (userData && userData.role === ROLES.CUSTOMER) {
      if(userData.isKyc){
        navigate(`/customer/my-businesses`);
      }else{
        navigate(`/customer/prospect-profile`);
      }
    } else if (userData && userData.role === ROLES.BANKER) {
      navigate(`/banker/register`);
    } else if (userData && userData.role === ROLES.BUSINESS) {
      navigate(`/leads`);
    } else if (userData && userData.role === ROLES.ADMIN) {
      navigate(`/admin/business`);
    } else {
      navigate(`*`);
    }
  };

  const completeLogin = async (accessToken: string, resetForm?: () => void) => {
    await storeAccessToken(accessToken);
    setSnackbar({
      open: true,
      message: loginMessages.loginSuccess,
      severity: "success",
    });
    resetForm?.();
    const user = await getLoginUserData();
    if (user && !user?.signature && !user.isInvited) {
      setShowSignModal(true);
    } else {
      setUser(user);
      redirectToUser(user);
    }
  };

  const handleUploadSignature = async (signedAgreement: { signature: string; agreementPdfPath: string }) => {
    try {
      setIsSignatureUploading(true);
      const userData = await getLoginUserData();
      if (!userData || !userData.id) {
        throw new Error("User data not found");
      }
      const payload = {
        signature: signedAgreement.signature,
        agreementPdfPath: signedAgreement.agreementPdfPath,
        id: userData.id,
      };
      if(userData?.role === ROLES.BUSINESS){
        await businessUploadNcnndaAgreement(payload);
      }else{
        await uploadNcnndaAgreement(payload);
      }
      setSnackbar({
        open: true,
        message: "Signed agreement uploaded successfully!",
        severity: "success",
      });
      setTimeout(() => {
        redirectToUser(userData);
      }, 800);
    } catch (error: any) {
      setIsSignatureUploading(false);
      setSnackbar({
        open: true,
        message:
          error?.message ||
          error?.response?.data?.message ||
          "Failed to upload signed agreement",
        severity: "error",
      });
    }
  };

  return (
    <>
      {isSignatureUploading && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            bgcolor: "rgba(0, 0, 0, 0.5)",
            zIndex: 9999,
          }}
        >
          <CircularProgress />
        </Box>
      )}
      <Box
        sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}
      >
        <Box
          flex={1}
          sx={{
            background: `url(${backgroundImg}) no-repeat center center`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            py: 2,
            width: "100%",
            minHeight: "100vh",
            backgroundSize: "cover",
          }}
        >
          <Paper
            elevation={3}
            sx={{
              width: "100%",
              maxWidth: 500,
              borderRadius: 4,
              p: { xs: 3, sm: 3 },
              mx: 2,
              boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.10)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
            }}
          >
            <img
              src={logo}
              alt="Logo"
              onClick={() => navigate("/")}
              style={{ width: 165, height: 165, objectFit: "contain", cursor: "pointer" }}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") navigate("/");
              }}
            />
            <Typography
              variant="h6"
              fontWeight={700}
              align="center"
              sx={{ mb: 1, letterSpacing: 0.5 }}
            >
              Log In to Continue
            </Typography>
            {/* <Typography
              align="center"
              sx={{ mb: 2, color: "text.secondary", fontSize: "0.975rem" }}
            >
              Don’t have a business account?{" "}
              <Button
                variant="text"
                color="primary"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.975rem",
                  p: 0,
                  minWidth: 0,
                }}
                onClick={() => {
                  navigate("/register-business");
                }}
              >
                Sign up
              </Button>
            </Typography> */}
            <Formik
              initialValues={loginInitialValues}
              validationSchema={LoginSchema}
              onSubmit={async (values, { setSubmitting, resetForm }) => {
                try {
                  const payload = { ...values };
                  const response = await loginUser(payload);
                  if (response?.requiresEmailVerification) {
                    setPendingCredentials(payload);
                    setEmailVerificationRequired(true);
                    setLoginOtp("");
                    setSnackbar({
                      open: true,
                      message: response.message || "Email verification OTP sent to your email",
                      severity: "success",
                    });
                    return;
                  }
                  if (response && response.access_token) {
                    await completeLogin(response.access_token, resetForm);
                  }
                } catch (error: any) {
                  setSnackbar({
                    open: true,
                    message:
                      error?.message ||
                      error?.response?.data?.message ||
                      loginMessages.loginFailed,
                    severity: "error",
                  });
                } finally {
                  setSubmitting(false);
                }
              }}
            >
              {({
                values,
                errors,
                touched,
                handleChange,
                handleBlur,
                isSubmitting,
              }) => (
                <Form style={{ width: "100%" }}>
                  <Stack spacing={2}>
                    <TextField
                      label="Email *"
                      name="email"
                      value={values.email}
                      onChange={(event) => {
                        handleChange(event);
                        setEmailVerificationRequired(false);
                        setPendingCredentials(null);
                        setLoginOtp("");
                      }}
                      onBlur={handleBlur}
                      error={touched.email && Boolean(errors.email)}
                      helperText={touched.email && errors.email}
                      fullWidth
                      disabled={isSubmitting}
                      variant="outlined"
                      size="medium"
                      placeholder="Enter email"
                      InputProps={{ style: { borderRadius: 10 } }}
                    />
                    <TextField
                      label="Password *"
                      name="password"
                      type={showPassword ? "text" : "password"}
                      value={values.password}
                      onChange={(event) => {
                        handleChange(event);
                        setEmailVerificationRequired(false);
                        setPendingCredentials(null);
                        setLoginOtp("");
                      }}
                      onBlur={handleBlur}
                      error={touched.password && Boolean(errors.password)}
                      helperText={touched.password && errors.password}
                      fullWidth
                      disabled={isSubmitting}
                      variant="outlined"
                      size="medium"
                      placeholder="Enter password"
                      InputProps={{
                        style: { borderRadius: 10 },
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              aria-label="toggle password visibility"
                              onClick={() => setShowPassword((show) => !show)}
                              edge="end"
                              size="small"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                    {emailVerificationRequired && (
                      <>
                        <TextField
                          label="Email OTP"
                          value={loginOtp}
                          onChange={(event) => setLoginOtp(event.target.value.replace(/\D/g, "").slice(0, 4))}
                          fullWidth
                          disabled={isSubmitting || isVerifyingEmail}
                          variant="outlined"
                          size="medium"
                          placeholder="Enter 4 digit OTP"
                          InputProps={{ style: { borderRadius: 10 } }}
                        />
                        <Button
                          type="button"
                          variant="contained"
                          color="primary"
                          disabled={
                            isSubmitting ||
                            isVerifyingEmail ||
                            loginOtp.length !== 4 ||
                            !pendingCredentials
                          }
                          fullWidth
                          sx={{
                            borderRadius: 2,
                            py: 1.5,
                            fontWeight: 600,
                            fontSize: "1rem",
                          }}
                          onClick={async () => {
                            try {
                              setIsVerifyingEmail(true);
                              await verifyEmailOTP(pendingCredentials.email, loginOtp);
                              const response = await loginUser(pendingCredentials);
                              if (response?.access_token) {
                                setEmailVerificationRequired(false);
                                setPendingCredentials(null);
                                setLoginOtp("");
                                await completeLogin(response.access_token);
                              }
                            } catch (error: any) {
                              setSnackbar({
                                open: true,
                                message:
                                  error?.message ||
                                  error?.response?.data?.message ||
                                  "Invalid or expired OTP",
                                severity: "error",
                              });
                            } finally {
                              setIsVerifyingEmail(false);
                            }
                          }}
                        >
                          {isVerifyingEmail ? "Verifying..." : "Verify Email"}
                        </Button>
                      </>
                    )}
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting || isVerifyingEmail}
                      startIcon={
                        isSubmitting ? (
                          <CircularProgress size={20} color="inherit" />
                        ) : (
                          <LoginIcon />
                        )
                      }
                      fullWidth
                      sx={{
                        borderRadius: 2,
                        py: 1.5,
                        fontWeight: 600,
                        fontSize: "1rem",
                        mt: 1,
                      }}
                    >
                      {isSubmitting ? "Login..." : "Login Account"}
                    </Button>
                  </Stack>
                </Form>
              )}
            </Formik>
            <Typography
              align="center"
              sx={{ mt: 2, color: "text.secondary", fontSize: "0.975rem" }}
            >
              Forgot your password?{" "}
              <Button
                variant="text"
                color="primary"
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.975rem",
                  p: 0,
                  minWidth: 0,
                }}
                onClick={() => {
                  navigate("/forgot-password");
                }}
              >
                Reset it here
              </Button>
            </Typography>
            <Snackbar
              open={snackbar.open}
              autoHideDuration={4000}
              onClose={handleCloseSnackbar}
              anchorOrigin={{ vertical: "top", horizontal: "center" }}
              sx={{ mt: 2 }}
            >
              <Alert
                onClose={handleCloseSnackbar}
                severity={snackbar.severity}
                sx={{ width: "100%" }}
              >
                {snackbar.message}
              </Alert>
            </Snackbar>
          </Paper>
        </Box>
        <DigitalSignModal
          open={showSignModal}
          onClose={() => {}}
          onSigned={async (signature) => {
            setShowSignModal(false);
            await handleUploadSignature(signature);
          }}
          onLogout={() => {
            setShowSignModal(false);
            removeAccessToken();
          }}
        />
      </Box>
    </>
  );
};

export default UserLoginForm;
